import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { connectorRequest } from './connectorApi';
import ConnectorContactLookup from './ConnectorContactLookup';
import ProfilePhoto, { getProfilePhoto } from '../ProfilePhoto';
import TalentPosition from '../TalentSearch/TalentPosition';
import { matchLabel, matchDirection } from '../TalentSearch/matchLabel';
import '../TalentProfile/TalentProfile.css';
import './ConnectorProfilePage.css';

const textOf = value => typeof value === 'string' ? value : value?.text || '';
const listOf = value => Array.isArray(value) ? value : [];
const dateOf = entry => entry.date_range || [entry.start_date, entry.end_date || (entry.is_current ? 'Present' : '')].filter(Boolean).join(' – ');

// Profile links carry only saved IDs. The server checks employer ownership and
// reads stored evidence/contacts; this page never opens or enriches a provider profile.
export default function ConnectorProfilePage() {
  useUiLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentUser } = useAuth();
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const tr = (en, ar) => isRTL ? ar : en;
  const candidateId = params.get('candidate_id');
  const searchId = params.get('search_id');
  const shortlistId = params.get('shortlist_id');
  const sourceId = searchId || shortlistId;
  const [state, setState] = useState({ loading: true });
  const [revision, setRevision] = useState(0);
  const [closeBlocked, setCloseBlocked] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setState({ loading: true, owner: currentUser?.uid, candidateId, sourceId });
    if (!currentUser || !candidateId || Boolean(searchId) === Boolean(shortlistId)) {
      setState({ error: true, owner: currentUser?.uid, candidateId, sourceId });
      return () => controller.abort();
    }
    const query = new URLSearchParams({ candidate_id: candidateId,
      ...(searchId ? { search_id: searchId } : { shortlist_id: shortlistId }) });
    connectorRequest(currentUser, `candidate?${query}`, { signal: controller.signal })
      .then(candidate => { if (!controller.signal.aborted) setState({ candidate, owner: currentUser.uid, candidateId, sourceId }); })
      .catch(() => { if (!controller.signal.aborted) setState({ error: true, owner: currentUser.uid, candidateId, sourceId }); });
    return () => controller.abort();
  }, [currentUser, candidateId, searchId, shortlistId, sourceId, revision]);

  const currentState = state.owner === currentUser?.uid && state.candidateId === candidateId && state.sourceId === sourceId;
  const candidate = currentState ? state.candidate : null;
  useEffect(() => {
    document.title = candidate?.name ? `${candidate.name} - JOBSEEQR` : 'Talent Profile - JOBSEEQR';
  }, [candidate?.name]);
  const closeTab = () => {
    // External links can open user-created tabs that browsers refuse to close.
    // Keep the profile in place in that case, rather than redirecting it.
    window.close();
    setCloseBlocked(true);
  };
  const back = () => {
    if (params.get('from') === 'sourcing') {
      if (window.history.length > 1) navigate(-1);
      else navigate(`/sourcing${shortlistId ? `?shortlist=${encodeURIComponent(shortlistId)}` : ''}`);
      return;
    }
    closeTab();
  };
  const validLinkedIn = /^https:\/\/(www\.)?linkedin\.com\/in\//i.test(candidate?.linkedin_url || '');
  const profile = candidate?.profile || {};
  const checks = listOf(candidate?.checks);
  const confirmed = checks.filter(check => check.status === 'yes');
  const missing = checks.filter(check => check.status !== 'yes');
  const contacts = listOf(candidate?.contacts);
  const summary = textOf(candidate?.analysis?.summary);

  if (!candidate) {
    const failed = currentState && state.error;
    return <div className="talent-profile-container connector-profile-page" dir={isRTL ? 'rtl' : 'ltr'}>
      {failed ? <div className="connector-profile-error-shell">
        <section className="connector-profile-error" role="alert">
          <i className="fa-regular fa-user connector-profile-state-icon" aria-hidden="true" />
          <h1>{tr('Could not load this profile', 'تعذر تحميل الملف')}</h1>
          <p>{tr('Please try again.', 'يرجى المحاولة مجدداً.')}</p>
          <div className="connector-profile-error-actions">
            <button type="button" className="tp-header-btn" onClick={() => { setCloseBlocked(false); setRevision(value => value + 1); }}>{tr('Retry', 'إعادة المحاولة')}</button>
            <button type="button" className="tp-header-btn tp-close-btn" onClick={closeTab}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />{tr('Close Profile', 'إغلاق الملف')}</button>
          </div>
          {closeBlocked && <p role="status">{tr('You can close this tab.', 'يمكنك إغلاق علامة التبويب هذه.')}</p>}
        </section>
      </div> : <div className="connector-profile-loading" aria-busy="true">
        <div className="connector-profile-loading-label" role="status">
          <span className="connector-profile-loading-spinner" aria-hidden="true" />{tr('Loading profile…', 'جارٍ تحميل الملف…')}
        </div>
        <div aria-hidden="true" className="connector-profile-skeleton">
          <div className="connector-profile-skeleton-header">
            <div className="connector-skeleton-block connector-skeleton-avatar" />
            <div className="connector-skeleton-heading">
              <div className="connector-skeleton-block connector-skeleton-name" />
              <div className="connector-skeleton-block connector-skeleton-role" />
              <div className="connector-skeleton-contacts">{[0, 1, 2].map(item => <div key={item} className="connector-skeleton-block" />)}</div>
            </div>
          </div>
          {[0, 1].map(section => <div key={section} className="connector-skeleton-section">
            <div className="connector-skeleton-block connector-skeleton-section-title" />
            <div className="connector-skeleton-block connector-skeleton-line" />
            <div className="connector-skeleton-block connector-skeleton-line" />
            <div className="connector-skeleton-block connector-skeleton-line connector-skeleton-line-short" />
          </div>)}
        </div>
      </div>}
    </div>;
  }

  return <div className="talent-profile-container connector-profile-page" dir={isRTL ? 'rtl' : 'ltr'}>
    <div className="tp-header-container">
      <div className="tp-header-top">
        <div className="tp-header-left">
          <div className="tp-image-frame-container"><div className="tp-image-frame-rotate1" /><div className="tp-image-frame-rotate2" />
            <div className="tp-profile-image-wrapper"><ProfilePhoto src={getProfilePhoto(candidate, profile)} alt={candidate.name} /></div>
          </div>
          <div className="tp-info-column"><div className="tp-name-row"><h1 className="tp-name">{candidate.name}</h1></div>
            <h2 className="tp-role-company"><TalentPosition metadata={candidate} language={i18n.language} empty={tr('Role not listed', 'المسمى الوظيفي غير مذكور')} /></h2>
          </div>
        </div>
        <div className="tp-header-actions"><button type="button" className="tp-header-btn tp-close-btn" onClick={back}>
          <i className="fa-solid fa-xmark" aria-hidden="true" /> {tr('Close Profile', 'إغلاق الملف')}</button></div>
      </div>
      <div className="tp-contact-row tp-contact-row-full">
        {candidate.location && <div className="tp-contact-item"><i className="fa-solid fa-location-dot tp-contact-icon" aria-hidden="true" />{candidate.location}</div>}
        <ConnectorContactLookup key={`${currentUser.uid}:${sourceId}:${candidateId}`} user={currentUser}
          candidateId={candidateId} searchId={searchId} shortlistId={shortlistId} contacts={contacts}
          lookup={candidate.contact_lookup} isRTL={isRTL} />
        {validLinkedIn && <a className="tp-contact-item connector-profile-link" href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer">
          <i className="fa-brands fa-linkedin tp-contact-icon" aria-hidden="true" />{uiText("LinkedIn")}</a>}
      </div>
      {closeBlocked && <p role="status">{tr('You can close this tab.', 'يمكنك إغلاق علامة التبويب هذه.')}</p>}
      <div className="tp-header-barrier" />
    </div>

    <div className="tp-content-container"><div className="tp-info-sections">
      {(checks.length > 0 || summary) && <section className="tp-section-container">
        <div className="tp-section-header"><div className="tp-section-title-group"><div className="tp-section-icon"><i className="fa-solid fa-check" aria-hidden="true" /></div>
          <h3 className="tp-section-title">{tr('Match to your search', 'التطابق مع بحثك')}</h3></div>
          {candidate.score != null && <span className="connector-profile-score">{candidate.score}/100</span>}</div>
        <div className="connector-profile-section-body">
          {summary && <p className="tp-description">{summary}</p>}
          {confirmed.length > 0 && <div className="tp-skill-tags" dir={confirmed.some(matchDirection) ? 'rtl' : undefined}>{confirmed.map(check => <span className="tp-skill-tag connector-confirmed" key={check.criterion_id || check.label}>
            <i className="fa-solid fa-check" aria-hidden="true" /> <bdi dir="auto">{matchLabel(check)}</bdi></span>)}</div>}
          {missing.length > 0 && <p className="tp-description"><strong>{tr('Not fully demonstrated: ', 'غير مثبت بالكامل: ')}</strong><bdi dir="auto">{missing.map(matchLabel).join(' · ')}</bdi></p>}
        </div>
      </section>}

      <section className="tp-section-container"><div className="tp-section-header"><div className="tp-section-title-group"><div className="tp-section-icon"><i className="fa-solid fa-briefcase" aria-hidden="true" /></div>
        <h3 className="tp-section-title">{tr('Career so far', 'المسيرة المهنية')}</h3></div></div>
        <div className="connector-profile-section-body">
          {textOf(profile.about) && <p className="tp-description">{textOf(profile.about)}</p>}
          <div className="tp-career-section-group"><h4 className="tp-ai-sub-header">{tr('Work experience', 'الخبرة العملية')}</h4>
            {listOf(profile.work_experience).length ? profile.work_experience.map((job, index) => <div className="tp-timeline-item" key={index}>
              <div className="tp-timeline-left"><div className={`tp-timeline-dot ${index ? 'past' : ''}`} /><div className="tp-timeline-line" /></div>
              <div className="tp-timeline-content"><h5 className="tp-role-title">{[job.title, job.company].filter(Boolean).join(' @ ')}</h5>
                {dateOf(job) && <p className="tp-date-range">{dateOf(job)}</p>}{textOf(job.description) && <p className="tp-description">{textOf(job.description)}</p>}</div>
            </div>) : <p className="tp-description">{tr('No work experience listed.', 'لا توجد خبرة عملية مذكورة.')}</p>}</div>
          <div className="tp-career-section-group"><h4 className="tp-ai-sub-header">{tr('Education', 'التعليم')}</h4>
            {listOf(profile.education).map((school, index) => <div className="tp-timeline-item" key={index}>
              <div className="tp-timeline-left"><div className={`tp-timeline-dot ${index ? 'past' : ''}`} /><div className="tp-timeline-line" /></div>
              <div className="tp-timeline-content"><h5 className="tp-role-title">{[school.degree, school.field_of_study, school.school].filter(Boolean).join(' · ')}</h5>
                {dateOf(school) && <p className="tp-date-range">{dateOf(school)}</p>}</div>
            </div>)}{!listOf(profile.education).length && <p className="tp-description">{tr('No education listed.', 'لا توجد معلومات تعليمية مذكورة.')}</p>}</div>
          {listOf(profile.certifications).length > 0 && <div className="tp-career-section-group"><h4 className="tp-ai-sub-header">{tr('Certifications', 'الشهادات')}</h4>
            <div className="tp-skill-tags">{profile.certifications.map((item, index) => <span className="tp-skill-tag" key={index}>{typeof item === 'string' ? item : item.name || item.title}</span>)}</div></div>}
          {listOf(profile.skills).length > 0 && <div className="tp-career-section-group"><h4 className="tp-ai-sub-header">{tr('Skills', 'المهارات')}</h4>
            <div className="tp-skill-tags">{profile.skills.filter(item => typeof item === 'string').map((item, index) => <span className="tp-skill-tag" key={index}>{item}</span>)}</div></div>}
          {listOf(profile.languages).length > 0 && <div className="tp-career-section-group"><h4 className="tp-ai-sub-header">{tr('Languages', 'اللغات')}</h4>
            <p className="tp-description">{profile.languages.map(item => typeof item === 'string' ? item : item.name).filter(Boolean).join(' · ')}</p></div>}
        </div>
      </section>
    </div></div>
  </div>;
}
